import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy } from "lucide-react";
import { fmtTL } from "@/components/RevenueDialog";

const COLORS = ["#34d399", "#fbbf24", "#60a5fa", "#f472b6", "#a78bfa", "#f87171"];

export function playVictory(ctxRef) {
  try {
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();
    [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
      const t = ctx.currentTime + i * 0.13;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(f, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + (i === 3 ? 0.7 : 0.22));
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.75);
    });
  } catch {}
}

function Confetti() {
  const pieces = useMemo(() => Array.from({ length: 90 }, (_, i) => ({
    id: i, x: Math.random() * 100, delay: Math.random() * 0.6, dur: 2.2 + Math.random() * 1.6,
    rot: Math.random() * 720 - 360, size: 6 + Math.random() * 8, color: COLORS[i % COLORS.length], drift: Math.random() * 30 - 15,
  })), []);
  return pieces.map((p) => (
    <motion.span key={p.id} className="absolute top-0 rounded-sm"
                 style={{ left: `${p.x}%`, width: p.size, height: p.size * 0.45, background: p.color }}
                 initial={{ y: -40, opacity: 1, rotate: 0, x: 0 }}
                 animate={{ y: "105vh", rotate: p.rot, x: `${p.drift}vw`, opacity: [1, 1, 0.8] }}
                 transition={{ duration: p.dur, delay: p.delay, ease: "easeIn" }} />
  ));
}

export function Celebration({ item, onDone }) {
  useEffect(() => {
    if (!item) return;
    const t = setTimeout(onDone, 4200);
    return () => clearTimeout(t);
  }, [item, onDone]);
  return createPortal(
    <AnimatePresence>
      {item && (
        <motion.div key={item.id} className="force-dark fixed inset-0 z-[80] pointer-events-none overflow-hidden flex items-center justify-center"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="celebration">
          <div className="absolute inset-0 bg-slate-950/50 backdrop-blur-[2px]" />
          <Confetti />
          <motion.div initial={{ scale: 0.4, opacity: 0, y: 30 }} animate={{ scale: [0.4, 1.08, 1], opacity: 1, y: 0 }}
                      exit={{ scale: 1.15, opacity: 0 }} transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
                      className="relative rounded-3xl border-2 border-amber-400/70 bg-slate-900/95 px-12 py-8 text-center shadow-[0_0_80px_rgba(251,191,36,0.35)]">
            <motion.div animate={{ rotate: [0, -12, 12, -6, 0] }} transition={{ duration: 0.9, delay: 0.3 }}
                        className="mx-auto mb-3 h-16 w-16 rounded-2xl bg-amber-400/20 border border-amber-400/50 flex items-center justify-center">
              <Trophy size={34} className="text-amber-300" />
            </motion.div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-amber-200">Büyük Satış</div>
            <div className="font-display text-6xl font-black text-emerald-300 my-2" data-testid="celebration-amount">+{fmtTL(item.amount)}</div>
            <div className="font-display text-2xl font-bold" data-testid="celebration-branch">{item.branch_name}</div>
            <div className="text-lg text-slate-200 mt-1" data-testid="celebration-user">{item.user_name}</div>
            <div className="text-sm text-slate-400 mt-1" data-testid="celebration-product">{item.product_name} ×{item.quantity}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
